import { ReactNode } from 'react';
import { Station } from '../../../services/models/assets';
import { getStationLiveStatus, hasFreshData, isVoltageOutOfRange } from '../../../utils/stationStatus';
import StationHeaderBadge from './StationHeaderBadge';
import SignalStrengthBadge from './SignalStrengthBadge';

interface StationHeaderProps {
  station: Station;
  action?: ReactNode;
  isAdmin?: boolean;
}

const formatCompactDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} - ${hours}h${minutes}`;
};

const StationHeader: React.FC<StationHeaderProps> = ({ station, action, isAdmin }) => {
  const status = getStationLiveStatus(station);
  // Telemetry visibility follows data freshness, NOT the active flag — a
  // deactivated station that is still transmitting keeps showing live values.
  const fresh = hasFreshData(station);
  const voltage = station.voltage ?? 0;
  const isLowVoltage = fresh && isVoltageOutOfRange(voltage);

  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
      <div className="flex flex-col gap-1">
        <h1 className="text-neutral typo-h1">{station.name || `Station ${station.id}`}</h1>
        <span className="text-neutral-40 typo-small">
          Dernière mise à jour: {formatCompactDate(station.updatedAt)}
        </span>
      </div>
      <div className="flex flex-wrap items-start justify-center gap-6 md:justify-start">
        <div className="flex flex-col items-center gap-1 w-16">
          <span className="text-neutral-60 typo-small">Status</span>
          {status === 'online' ? (
            <div className="flex items-center gap-1 border border-solid border-success-500 rounded-sm justify-center px-2">
              <div className="w-1.5 h-1.5 rounded-full bg-success-500" />
              <span className="text-success-600 typo-body text-nowrap">En ligne</span>
            </div>
          ) : status === 'inactive' ? (
            <div className="flex items-center gap-1 border border-solid border-neutral-40 rounded-sm py-0.5 px-2">
              <div className="w-1.5 h-1.5 rounded-full bg-neutral-40" />
              <span className="text-neutral-60 typo-small !font-semibold text-nowrap">Inactive</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 border border-solid border-error-500 rounded-sm py-0.5 px-2">
              <div className="w-1.5 h-1.5 rounded-full bg-error-500" />
              <span className="text-error-600 typo-small !font-semibold text-nowrap">Hors ligne</span>
            </div>
          )}
        </div>
        {!fresh ? (
          <div className="flex flex-col items-center gap-1">
            <span className="text-neutral-60 typo-body">Voltage</span>
            <span className="text-neutral-40 typo-body">--</span>
          </div>
        ) : isLowVoltage ? (
          <div className="flex flex-col items-center gap-1">
            <span className="text-neutral-60 typo-body">Voltage</span>
            <span className="text-error-600 typo-body">{voltage.toFixed(1)}</span>
          </div>
        ) : (
          <StationHeaderBadge label="Voltage" value={voltage.toFixed(1).toString()} />
        )}
        <StationHeaderBadge label="Version" value={station.firmwareVersion ?? '--'} />
        <SignalStrengthBadge rssi={station.rssi} online={fresh} />
        {isAdmin && (
          <StationHeaderBadge label="Opérateur" value={station.networkOperator ?? '--'} />
        )}
        {action && (
          <div className="self-center pl-6 ml-2 border-l border-neutral-10">
            {action}
          </div>
        )}
      </div>
    </div>
  );
};


export default StationHeader;
